import { useAuth } from "@ibcos/ui-components/dist/components/Hooks/useAuth/AuthProvider";
import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useSearchParams } from "react-router";
import LoadingSpinner from "../components/LoadingSpinner";
import AuthAPI from "../API/AuthAPI";

import { ReactNode } from "react";
import { AppRoute } from "../models/AppRoute";
import accountRoutes from "../modules/account/router";
import customerRoutes from "../modules/customer/router";
import dealerRoutes from "../modules/dealer/router";
import { UserPermissions } from "../utilities/Constants";

interface ProtectedRouteProps {
    authorize?: string[];
    userPermissions?: string[];
    children?: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    authorize = [],
    userPermissions = [],
    children,
}) => {
    const { user, loading } = useAuth();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // Check if this is a shadow operation
    const isShadowMode = searchParams.has("shadowUserId");

    useEffect(() => {
        //User Timed Out
        AuthAPI.testUserStatus()
            .then((output) => {
                if (output.status != 200) {
                    console.log("Session expired - Redirecting to login");
                    //alert('ERR 1');
                    //window.location.href = `${import.meta.env.VITE_BFF_PROXY}/login?returnUrl=${import.meta.env.VITE_SITE_BASE}`;
                }
            })
            .catch((err) => {
                const attemptedUrl = location.pathname + location.search;
                sessionStorage.setItem("returnUrl", attemptedUrl);

                console.log("Session expired - Redirecting to login", err);
                //alert('ERR 2');
                window.location.href = `${
                    import.meta.env.VITE_BFF_PROXY
                }/bff/login?returnUrl=${import.meta.env.VITE_SITE_BASE}`;
            });
    }, [location]);

    useEffect(() => {
        //Logging out
        const handleStorageUpdate = (event: StorageEvent) => {
            if (event.key === "logout-event") {
                sessionStorage.clear();
                window.location.href = "/";
            }
        };

        window.addEventListener("storage", handleStorageUpdate);
        return () => {
            window.removeEventListener("storage", handleStorageUpdate);
        };
    }, []);

    const accessPermitted: () => boolean = () => {
        const allRoutes: AppRoute[] = [
            ...dealerRoutes,
            ...accountRoutes,
            ...customerRoutes,
        ];

        function findRouteByPath(
            routes: AppRoute[],
            path: string
        ): AppRoute | undefined {
            for (const route of routes) {
                if (route.path === path) return route;
                if (route.children) {
                    const found = findRouteByPath(route.children, path);
                    if (found) return found;
                }
            }
            return undefined;
        }

        const currentRoute: AppRoute | undefined = findRouteByPath(
            allRoutes,
            location.pathname
        );

        if (!currentRoute && location.pathname !== "/") return false;

        if (
            currentRoute &&
            currentRoute.meta &&
            currentRoute.meta.requiresAuthorization
        ) {
            // In shadow mode, allow dealership users (who have CUSTOMER_ACCOUNTS permission)
            // to access customer pages (which normally require CUSTOMER_ACTIVITY permission)
            if (
                isShadowMode &&
                currentRoute.meta.authorize?.includes(
                    UserPermissions.DOWNLOAD_DOCUMENTS
                )
            ) {
                // Check if user has CUSTOMER_ACCOUNTS permission (dealership users)
                const hasDealershipPermission = userPermissions.includes(
                    UserPermissions.MANAGE_CUSTOMER_ACCOUNTS
                );
                if (hasDealershipPermission) {
                    return true;
                }
            }

            const isAuthorized = currentRoute.meta.authorize?.some(
                (permission) => userPermissions.includes(permission)
            );
            if (!isAuthorized && location.pathname !== "/") return false;
        }

        return true;
    };

    useEffect(() => {
        if (userPermissions && userPermissions.length > 0 && !accessPermitted())
            window.location.href = "/";
    }, [userPermissions]);

    if (loading) {
        //Show Spinner
        return <LoadingSpinner />;
    }

    if (!user) {
        //Logged Out
        window.location.href = `${
            import.meta.env.VITE_BFF_PROXY
        }/bff/login?returnUrl=${import.meta.env.VITE_SITE_BASE}`;
        return "";
    }

    // Authorization check
    // In shadow mode, allow dealership users to access customer pages
    const hasPermission =
        !authorize.length ||
        authorize.some((perm) => userPermissions.includes(perm)) ||
        (isShadowMode &&
            authorize.includes(UserPermissions.DOWNLOAD_DOCUMENTS) &&
            userPermissions.includes(UserPermissions.MANAGE_CUSTOMER_ACCOUNTS));

    if (!hasPermission) {
        return <Navigate to="/" replace />;
    }

    return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
